import React, { useMemo, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Calendar } from 'primereact/calendar';
import { Checkbox } from 'primereact/checkbox';
import { RadioButton } from 'primereact/radiobutton';
import { Dropdown } from 'primereact/dropdown';
import { useLanguage } from '../../../../contexts/LanguageContext';
import DealerListBox from '../../../../components/common/DealerListBox';
import RegionalManagerUserDropdown from '../../customer/customerAddressBrandPage/components/dialogs/RegionalManagerUserDropdown';
import GenericFileUpload from '../../../../components/common/GenericFileUpload';
import { Divider } from 'primereact/divider';
import AnnouncementTypeDropdown from '../../announcement-type/components/AnnouncementTypeDropdown';

const AnnouncementForm = ({
    dialogVisible,
    editMode,
    submitted,

    hideDialog,
    handleSubmit,
    handlePublish,
    isPublishing,

    type,
    typeName,
    id,
    announcementType,
    announcementTypeName,
    brand,
    announcementSubject,
    announcementDate,
    announcementContent,
    status,
    isActive,
    selectedDealerIds,
    userId,
    dealerType,
    domesticOrForeign,
    setType,
    setTypeName,
    setAnnouncementType,
    setAnnouncementTypeName,
    setBrand,
    setAnnouncementSubject,
    setAnnouncementDate,
    setAnnouncementContent,
    setStatus,
    setIsActive,
    setSelectedDealerIds,
    setUserId,
    setDealerType,
    setDomesticOrForeign,

    // docs
    documents = [],
    isLoadingDocuments = false,
    handleSafeDownload,
    updatedFiles,
    onEditFilesChange
}) => {
    const { th } = useLanguage();

    const TYPE_OPTIONS = [
        { label: th('announcement.typeLabels.sales'), value: 128 },
        { label: th('announcement.typeLabels.marketing'), value: 129 },
        { label: th('announcement.typeLabels.afterSales'), value: 130 },
        { label: th('announcement.typeLabels.warranty'), value: 131 },
        { label: th('announcement.typeLabels.spareParts'), value: 132 }
    ];

    const DEALER_TYPE_OPTIONS = [
        { label: th('announcement.dealerTypeLabels.service'), value: 'SERVICE' },
        { label: th('announcement.dealerTypeLabels.salesAndService'), value: 'SALES_AND_SERVICE' },
        { label: th('announcement.dealerTypeLabels.sales'), value: 'SALES' },
        { label: th('announcement.dealerTypeLabels.sparePartsOrder'), value: 'SPARE_PARTS_ORDER' }
    ];

    const DOMESTIC_OR_FOREIGN_OPTIONS = [
        { label: th('announcement.formLabels.all'), value: null },
        { label: th('announcement.formLabels.domestic'), value: 1 },
        { label: th('announcement.formLabels.foreign'), value: 2 }
    ];

    const isPublished = typeof status === 'number' ? status === 1 : !!status;

    const footer = (
        <div className="flex justify-content-end gap-2">
            {editMode && !isPublished && (
                <button type="button" className="p-button p-component p-button-success" onClick={handlePublish} disabled={isPublishing}>
                    <span className={`p-button-icon p-button-icon-left ${isPublishing ? 'pi pi-spin pi-spinner' : 'pi pi-bullhorn'}`}></span>
                    <span className="p-button-label">{th('announcement.formLabels.publish')}</span>
                </button>
            )}
            <button className="p-button p-component p-button-outlined" onClick={hideDialog} type="button">
                <span className="p-button-icon p-button-icon-left pi pi-times"></span>
                <span className="p-button-label">{th('common.cancel')}</span>
            </button>
            <button className="p-button p-component" onClick={handleSubmit} type="button">
                <span className="p-button-icon p-button-icon-left pi pi-check"></span>
                <span className="p-button-label">{th('common.save')}</span>
            </button>
        </div>
    );

    useEffect(() => {
        if (!editMode && TYPE_OPTIONS.length > 0 && !type) {
            setType(TYPE_OPTIONS[0].value);
            setTypeName(TYPE_OPTIONS[0].label);
        }
    }, [editMode, TYPE_OPTIONS, type]);

    return (
        <Dialog visible={dialogVisible} style={{ width: '90vw', maxWidth: '1200px' }} header={editMode ? th('announcement.editTitle') : th('announcement.newTitle')} modal className="p-fluid" footer={footer} onHide={hideDialog} maximizable>
            <div className="grid">
                <div className="col-12">
                    <h3 className="text-white p-3 mb-3 border-round text-lg font-semibold" style={{ backgroundColor: '#1e4d6b' }}>
                        {th('announcement.sections.announcementInfo')}
                    </h3>
                </div>

                <div className="col-12 md:col-4">
                    <div className="field mb-4">
                        <label className="font-bold block mb-2">{th('announcement.formLabels.type')}</label>
                        <div className="flex flex-wrap gap-3">
                            {TYPE_OPTIONS.map((opt) => (
                                <div key={opt.value} className="flex align-items-center">
                                    <RadioButton
                                        inputId={`type-${opt.value}`}
                                        name="type"
                                        value={opt.value}
                                        checked={type === opt.value}
                                        onChange={(e) => {
                                            const v = e.value;
                                            setType(v);
                                            setTypeName(TYPE_OPTIONS.find((o) => o.value === v)?.label ?? '');
                                            setAnnouncementType(null);
                                            setAnnouncementTypeName('');
                                        }}
                                    />
                                    <label htmlFor={`type-${opt.value}`} className="ml-2">
                                        {opt.label}
                                    </label>
                                </div>
                            ))}
                        </div>
                        {submitted && type == null && <small className="p-error">{th('common.required')}</small>}
                    </div>
                </div>

                <div className="col-12 md:col-4">
                    <div className="field mb-4">
                        <label className="font-bold block mb-2">{th('announcement.formLabels.announcementType')}</label>
                        <AnnouncementTypeDropdown
                            selectedType={type}
                            id={announcementType}
                            value={typeName}
                            onChange={({ id, value }) => {
                                setAnnouncementType(id);
                                setTypeName(value);
                            }}
                            placeholder={th('announcement.placeholders.announcementType')}
                        />
                        {submitted && announcementType == null && <small className="p-error">{th('common.required')}</small>}
                    </div>
                </div>

                <div className="col-12 md:col-4">
                    <div className="field mb-4">
                        <label className="font-bold block mb-2">{th('announcement.formLabels.announcementDate')}</label>
                        <Calendar
                            value={announcementDate || null}
                            onChange={(e) => setAnnouncementDate(e.value)}
                            dateFormat="dd/mm/yy"
                            showIcon
                            placeholder={th('announcement.placeholders.date')}
                            className={submitted && !announcementDate ? 'p-invalid' : ''}
                        />
                        {submitted && !announcementDate && <small className="p-error">{th('common.required')}</small>}
                    </div>
                </div>

                <div className="col-12">
                    <h3 className="text-white p-3 mb-3 border-round text-lg font-semibold" style={{ backgroundColor: '#1e4d6b' }}>
                        {th('announcement.sections.targetAudience')}
                    </h3>
                </div>

                <div className="col-12 md:col-4">
                    <div className="field mb-4">
                        <label className="font-bold block mb-2">{th('announcement.formLabels.regionalManager')}</label>
                        <RegionalManagerUserDropdown value={userId} onChange={(id) => setUserId(id)} className={submitted && !userId ? 'p-invalid' : ''} />
                        {submitted && !userId && <small className="p-error">{th('common.required')}</small>}
                    </div>
                </div>

                <div className="col-12 md:col-4">
                    <div className="field mb-4">
                        <label className="font-bold block mb-2">{th('announcement.formLabels.dealerType')}</label>
                        <Dropdown value={dealerType} options={DEALER_TYPE_OPTIONS} onChange={(e) => setDealerType(e.value)} placeholder={th('announcement.placeholders.dealerType')} className={submitted && !dealerType ? 'p-invalid' : ''} />
                        {submitted && !dealerType && <small className="p-error">{th('common.required')}</small>}
                    </div>
                </div>

                <div className="col-12 md:col-4">
                    <div className="field mb-4">
                        <label className="font-bold block mb-2">{th('announcement.formLabels.dealerCountry')}</label>
                        <div className="flex flex-wrap gap-3">
                            {DOMESTIC_OR_FOREIGN_OPTIONS.map((opt) => (
                                <div key={String(opt.value)} className="flex align-items-center">
                                    <RadioButton inputId={`dof-${opt.value}`} name="domesticOrForeign" value={opt.value} checked={domesticOrForeign === opt.value} onChange={(e) => setDomesticOrForeign(e.value)} />
                                    <label htmlFor={`dof-${opt.value}`} className="ml-2">
                                        {opt.label}
                                    </label>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="col-12">
                    <div className="field mb-4">
                        <label className="font-bold block mb-2">
                            {th('announcement.formLabels.dealers')} ({(selectedDealerIds || []).length})
                        </label>
                        <DealerListBox
                            value={(selectedDealerIds || []).map(Number).filter(Number.isFinite)}
                            onChange={(vals) => setSelectedDealerIds((vals || []).map(Number).filter(Number.isFinite))}
                            visible={dialogVisible}
                            rowCount={500}
                            placeholder={th('common.search')}
                            managerUserId={userId}
                            dealerType={dealerType}
                            domesticOrForeign={domesticOrForeign}
                        />
                        {submitted && (!selectedDealerIds || selectedDealerIds.length === 0) && <small className="p-error">{th('common.required')}</small>}
                    </div>
                </div>

                <div className="col-12">
                    <h3 className="text-white p-3 mb-3 border-round text-lg font-semibold" style={{ backgroundColor: '#1e4d6b' }}>
                        {th('announcement.sections.announcementContent')}
                    </h3>
                </div>

                <div className="col-12">
                    <div className="field mb-4">
                        <label className="font-bold block mb-2">{th('announcement.formLabels.subject')}</label>
                        <InputText
                            value={announcementSubject ?? ''}
                            onChange={(e) => setAnnouncementSubject(e.target.value)}
                            placeholder={th('announcement.placeholders.subject')}
                            className={submitted && !String(announcementSubject ?? '').trim() ? 'p-invalid' : ''}
                        />
                        {submitted && !String(announcementSubject ?? '').trim() && <small className="p-error">{th('common.required')}</small>}
                    </div>
                </div>

                <div className="col-12">
                    <div className="field mb-4">
                        <label className="font-bold block mb-2">{th('announcement.formLabels.content')}</label>
                        <InputTextarea
                            value={announcementContent ?? ''}
                            onChange={(e) => setAnnouncementContent(e.target.value)}
                            rows={8}
                            maxLength={2400}
                            placeholder={th('announcement.placeholders.content')}
                            className={submitted && !String(announcementContent ?? '').trim() ? 'p-invalid' : ''}
                            style={{ resize: 'none' }}
                        />
                        <div className="text-right mt-1" style={{ fontSize: '0.8rem' }}>
                            {announcementContent?.length ?? 0}/2400
                        </div>
                        {submitted && !String(announcementContent ?? '').trim() && <small className="p-error">{th('common.required')}</small>}
                    </div>
                </div>
                {editMode && (
                    <>
                        <div className="col-12">
                            <h3 className="text-white p-3 mb-3 border-round text-lg font-semibold" style={{ backgroundColor: '#1e4d6b' }}>
                                <i className="pi pi-paperclip mr-2"></i>
                                {th('announcement.formLabels.fileUpload')}
                            </h3>
                        </div>

                        <div className="col-12">
                            <GenericFileUpload
                                key={dialogVisible ? 'open' : 'closed'}
                                newFiles={updatedFiles}
                                onFilesChange={onEditFilesChange}
                                existingDocuments={documents}
                                onDownloadDocument={handleSafeDownload}
                                isLoading={isLoadingDocuments}
                                multiple={false}
                                showDownloadButton
                                showUpdateButton={false}
                                showDeleteButton={false}
                                maxFileSize={20971520}
                                acceptedFileTypes={{
                                    'image/*': ['.png', '.jpg', '.jpeg', '.gif'],
                                    'application/pdf': ['.pdf'],
                                    'application/msword': ['.doc'],
                                    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
                                    'application/vnd.ms-excel': ['.xls'],
                                    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
                                    'text/plain': ['.txt']
                                }}
                            />
                        </div>
                    </>
                )}
                <div className="col-12">
                    <Divider className="mt-3 mb-3" />
                    <div className="field-checkbox mb-0">
                        <Checkbox inputId="isActive" checked={!!isActive} onChange={(e) => setIsActive(e.checked)} />
                        <label htmlFor="isActive" className="ml-2 font-bold">
                            {th('announcement.formLabels.active')}
                        </label>
                    </div>
                </div>
            </div>
        </Dialog>
    );
};

export default AnnouncementForm;
